/**
 * Joining a hub this device already has an account on — the code-less half of
 * `HEADLESS-HUB-ONBOARDING.md` §2/D4 ("second person (invited by email) signs in
 * → hub already has a user row with that email → auto-link + auto-bind, role
 * from the invite. No code, no QR, no password.").
 *
 * ## The complaint this closes
 *
 * "We are signed in as a user. Why do we need to enter a pairing code? We are
 * already paired if our email is entered (being invited)."
 *
 * Two facts were conflated: the *person's* role on the hub (a cloud invite
 * creates the hub `users` row with their email) and the *device's* credential
 * (hub `device_bindings` + a `typ:"device"` JWT). The join surface only ever
 * established the second, by code — so an invited person who was signed in and
 * sitting on the hub's own LAN was still asked for a six-character code.
 *
 * ## What the app does instead (measured, not assumed)
 *
 * The hub's routes were read before this module was written, against the dev hub
 * on the LAN (card `t_dc2d87c6`, evidence
 * `docs/plans/evidence/app-autolink-autobind.md`):
 *
 *   - `POST /api/auth/enroll/:id/redeem` refuses a device whose enrollment was
 *     not approved (`409 not_approved`) — the plan's "begin → redeem" alone
 *     cannot bind anybody;
 *   - `POST /api/auth/enroll/:id/approve` is admin-only, and the dev hub
 *     answers `403 Insufficient permissions` to the *resident* row the invite
 *     produced — so "the person approves their own device" is not a flow that
 *     exists either.
 *
 * What does exist is F6's trust object: a **cloud-signed hub grant**
 * (`iss:"torik.cloud"`, `sub`, `hub`, `role`, `dev`), which the hub verifies
 * against the pinned cloud key and turns into a binding. The cloud mints one for
 * a signed-in account that holds a role on the hub — that is `POST /api/link/claim`,
 * the same route a phone uses when it scans a keyboard-less device's code. Here
 * the device claims its OWN code, so nothing is typed, photographed or read out:
 *
 *   1. `POST <hub>/api/auth/claim`            { cloudAccessToken }
 *        → `{token, user}` — the hub links the account to the user row whose
 *          email the CLOUD verified (F13). This is the "auto-link".
 *   2. `POST <hub>/api/auth/enroll/begin`     { devicePubKey, deviceName, … }
 *        → `{enrollId, deviceCode, hubPublicId, …}` — the device code never
 *          leaves the app.
 *   3. `POST <cloud>/api/link/claim`          { code, hubPublicId, devicePubKey, … }
 *        → the cloud checks the account's role on that hub, spends the code,
 *          signs the grant and delivers it to the hub (socket, or the pull
 *          channel). This is the authorization, and it is the account's own
 *          role — never a code somebody read out.
 *   4. `POST <hub>/api/auth/enroll/:id/redeem` { devicePubKey, proof: deviceCode }
 *        → the 90-day device credential, which the session layer stores exactly
 *          as it stores an enrolled or adopted one (one credential path).
 *
 * Step 3 is the one the plan's prose compressed into "begin → redeem"; it is
 * named here, and in the evidence doc, because a reader of that prose cannot
 * otherwise know how a `resident` device ever gets approved.
 *
 * ## Why this module is pure
 *
 * Everything a screen needs in order to word an ending is a value: which code a
 * server sent, what a body means, whether an answer is a complete credential or
 * half of one. The HTTP is `services/hubJoinFlow.ts`; the sheet is
 * `components/hub/JoinHubSheet.vue`. Nothing here fetches, stores or renders.
 */

/**
 * The hub path the app claims its account on (F13) — **relative to the base**.
 *
 * `<hub>/api`. Every path in this module is what follows a base that already
 * carries the prefix: the address ladder builds `http(s)://<host>:<port>/api`
 * (`hubApiAttempts`), and the app's cloud transport builds its URL from
 * `config.cloudApiUrl` (`https://torik.ai/api`, `public/config.json`) — the
 * same rule every other `cloudFetch('/account/…')` call site follows.
 *
 * A path constant that carries `/api` of its own composes to `/api/api/…`,
 * which the server answers **404** and the surface reads as `unreachable`
 * ("Nothing answered at this address"). That shipped once and only a live run
 * caught it (card `t_dc2d87c6`, evidence §5): nothing in a unit test that
 * injects a `fetchImpl` sees the composed URL unless the test composes it, so
 * `hubJoin.test.ts` now pins every one of these against its base.
 */
export const HUB_CLAIM_PATH = '/auth/claim';
/** The cloud path that mints and delivers the grant (base `<cloud>/api`). */
export const CLOUD_LINK_CLAIM_PATH = '/link/claim';
/** The hub path that takes a grant directly (base `<hub>/api`). */
export const HUB_GRANTS_PATH = '/hub/grants';

/*
 * The user-device enrollment half of this file is gone: `POST /auth/enroll/begin`,
 * its poll (`JOIN_APPROVAL_TIMEOUT_MS` / `JOIN_APPROVAL_POLL_MS`) and its
 * responses are not read anywhere, because a person's own device holds no
 * `typ:"device"` credential and there is no code chain left (card `t_2f0c4a91`).
 * What a join CAN be refused with is still this file's business — the vocabulary
 * below is shared with the local route check (`services/hubLocalFirst.ts`).
 */

/**
 * Every way the join can end without a device credential.
 *
 * A runtime list with the type derived from it, so a surface (or a copy test)
 * can walk the contract instead of a type it cannot read, and
 * `joinFailureMessageKey` must answer for every entry.
 *
 * The first two are the ones a person must be able to act on: `no_matching_user`
 * means the hub has no user with this email (an administrator has to invite it),
 * `not_a_member` means the cloud account holds no role on this hub.
 */
export const JOIN_FAILURE_CODES = [
  /** `POST /auth/claim` → 404: nobody on this hub carries this verified email. */
  'no_matching_user',
  /** `POST /auth/claim` → 403: the hub user exists but is disabled. */
  'user_disabled',
  /**
   * `POST /auth/claim` → 403: the hub user exists, is switched on, and the hours
   * they may sign in do not include now (card `t_2f0c4a91`). The refusal carries
   * `windowStartsAt`/`windowEndsAt`, so a surface can say **when** the door opens.
   */
  'outside_login_window',
  /** `POST /link/claim` → 403: the account has no role on this hub. */
  'not_a_member',
  /** The cloud token was refused (expired, revoked, wrong cloud). */
  'invalid_cloud_token',
  /** This device key already belongs to another account. */
  'device_in_use',
  /** The device code was already spent — a second claim of the same code. */
  'already_claimed',
  /** The device code's window closed before the hub took it. */
  'expired',
  /** The cloud cannot sign hub grants right now (its key is missing). */
  'grant_key_missing',
  /** The hub does not know this device's enrollment (a `begin` that never ran). */
  'unknown_device',
  /** The hub still says the enrollment is not approved after the wait. */
  'not_approved',
  /** The hub refused the device's own proof (key or code mismatch). */
  'invalid_proof',
  /** Throttled by either server (429). */
  'rate_limited',
  /** The hub is not on this network / cannot be reached at the address tried. */
  'unreachable',
  /** A hub that answered, but not with something this flow can use. */
  'malformed',
  /** Anything else a server said, kept with its own code for the log line. */
  'refused',
] as const;

export type JoinFailureCode = (typeof JOIN_FAILURE_CODES)[number];

export interface JoinFailure {
  ok: false;
  code: JoinFailureCode;
  /** HTTP status of the answer that decided this, when there was one. */
  status: number | null;
  /** The server's own `code`/`reason`, verbatim, for the log line. */
  serverCode: string | null;
  /** The server's own `error` string, when it sent one. */
  message: string | null;
}

function failure(
  code: JoinFailureCode,
  status: number | null = null,
  serverCode: string | null = null,
  message: string | null = null,
): JoinFailure {
  return { ok: false, code, status, serverCode, message };
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * The servers' own codes → this client's vocabulary.
 *
 * `Record` so a code the hub or the cloud starts sending without a line here is
 * a build error the moment the type is extended — and every value has a
 * sentence (`joinFailureMessageKey`), which the copy test walks.
 */
const SERVER_CODES: Record<string, JoinFailureCode> = {
  // hub: POST /auth/claim
  no_matching_user: 'no_matching_user',
  user_disabled: 'user_disabled',
  outside_login_window: 'outside_login_window',
  invalid_cloud_token: 'invalid_cloud_token',
  // cloud: POST /api/link/claim
  not_a_member: 'not_a_member',
  device_in_use: 'device_in_use',
  already_claimed: 'already_claimed',
  expired: 'expired',
  grant_key_missing: 'grant_key_missing',
  hub_not_found: 'unreachable',
  bad_lan_url: 'unreachable',
  invalid_body: 'malformed',
  invalid_code: 'malformed',
  invalid_hub_id: 'malformed',
  invalid_device_key: 'malformed',
  internal: 'refused',
  // hub: POST /api/hub/grants (a direct post when the push did not land)
  unknown_device: 'unknown_device',
  grant_already_redeemed: 'already_claimed',
  grant_redemption_in_progress: 'already_claimed',
  wrong_hub: 'refused',
  wrong_device: 'refused',
  invalid_signature: 'refused',
  issuer_key_mismatch: 'refused',
  cloud_key_not_pinned: 'refused',
  invalid_claims: 'refused',
  grant_missing_jti: 'refused',
  // hub: POST /auth/enroll/begin + redeem
  invalid_device_pub_key: 'invalid_proof',
  invalid_proof: 'invalid_proof',
  not_approved: 'not_approved',
  cancelled: 'expired',
  not_redeemable: 'not_approved',
  // both
  rate_limited: 'rate_limited',
};

export interface JoinFailureInputLike {
  status?: number | null;
  code?: string | null;
  reason?: string | null;
  error?: string | null;
  message?: string | null;
}

/**
 * A server's refusal → this client's vocabulary. Pure, so the matrix is a test.
 *
 * The code is read first (`code`, then the cloud's `reason`), then the status,
 * which still separates the three families a person can act on — sign in again,
 * ask the hub owner, wait — from everything else.
 */
export function classifyJoinFailure(status: number | null, body: unknown): JoinFailure {
  const raw = body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
  const serverCode = stringOrNull(raw?.code) ?? stringOrNull(raw?.reason);
  const message = stringOrNull(raw?.error) ?? stringOrNull(raw?.message) ?? stringOrNull(raw?.detail);

  // A code the server itself named wins over the status: the hub answers
  // `401 invalid_proof` for a wrong device key, which must NOT read as "sign in
  // again" — and the cloud answers `403 not_a_member`, which a bare 403 cannot
  // tell apart from a dozen other permissions.
  if (serverCode && SERVER_CODES[serverCode]) {
    return failure(SERVER_CODES[serverCode], status, serverCode, message);
  }

  if (status === 401) return failure('invalid_cloud_token', status, serverCode, message);
  if (status === 403) return failure('not_a_member', status, serverCode, message);
  if (status === 404 || status === 405) return failure('unreachable', status, serverCode, message);
  if (status === 409) return failure('already_claimed', status, serverCode, message);
  if (status === 410) return failure('expired', status, serverCode, message);
  if (status === 429) return failure('rate_limited', status, serverCode, message);
  return failure('refused', status, serverCode, message);
}

/**
 * One sentence per outcome, as an i18n key. The copy lives in all six locale
 * files — never in a service, and never in the sheet's template.
 */
export function joinFailureMessageKey(code: JoinFailureCode): string {
  switch (code) {
    case 'no_matching_user':
      return 'hub.join.noMatchingUser';
    case 'not_a_member':
      return 'hub.join.notAMember';
    case 'user_disabled':
      return 'hub.join.userDisabled';
    case 'outside_login_window':
      return 'hub.join.outsideLoginWindow';
    case 'invalid_cloud_token':
      return 'hub.join.invalidCloudToken';
    case 'device_in_use':
      return 'hub.join.deviceInUse';
    case 'already_claimed':
      return 'hub.join.alreadyClaimed';
    case 'expired':
      return 'hub.join.expired';
    case 'grant_key_missing':
      return 'hub.join.grantKeyMissing';
    case 'unknown_device':
      return 'hub.join.unknownDevice';
    case 'not_approved':
      return 'hub.join.notApproved';
    case 'invalid_proof':
      return 'hub.join.invalidProof';
    case 'rate_limited':
      return 'hub.join.rateLimited';
    case 'unreachable':
      return 'hub.join.unreachable';
    case 'malformed':
      return 'hub.join.malformed';
    default:
      return 'hub.join.refused';
  }
}

// ─── Response readers ────────────────────────────────────────────────────────
//
// Each returns null rather than half an answer: a flow that adopts part of a
// backend response is how a device ends up "connected" with no credential.

export interface JoinIdentity {
  hubId: string;
  publicId: string;
  name: string;
}

/** `GET /api/hub/identity` — the hub naming itself (also the reachability check). */
export function readJoinIdentity(body: unknown): JoinIdentity | null {
  if (!body || typeof body !== 'object') return null;
  const raw = body as Record<string, unknown>;
  const publicId = typeof raw.publicId === 'string' ? raw.publicId.trim().toLowerCase() : '';
  if (!/^[0-9a-f]{16}$/.test(publicId)) return null;
  return {
    hubId: stringOrNull(raw.hubId) ?? '',
    publicId,
    name: stringOrNull(raw.name) ?? '',
  };
}

export interface LinkGrantAnswer {
  grant: string;
  grantJti: string | null;
  /** The role the cloud says this account holds on the hub. */
  grantedRole: string | null;
  /** Whether the hub already has the grant (`delivered`) or will pull it. */
  delivered: boolean;
}

/** `POST /api/link/claim` — the grant plus what the cloud did with it. */
export function readLinkGrantAnswer(body: unknown): LinkGrantAnswer | null {
  if (!body || typeof body !== 'object') return null;
  const raw = body as Record<string, unknown>;
  const grant = stringOrNull(raw.grant);
  if (!grant) return null;
  const delivery = raw.delivery && typeof raw.delivery === 'object' ? (raw.delivery as Record<string, unknown>) : null;
  return {
    grant,
    grantJti: stringOrNull(raw.grantJti),
    grantedRole: stringOrNull(raw.grantedRole),
    delivered: delivery?.delivered === true,
  };
}

/**
 * The hub's own origin: the address ladder's base (`http(s)://<host>:<port>/api`)
 * minus the API prefix.
 *
 * `lanUrl` is a *location*, not an endpoint — the hub records it on the
 * enrollment and the cloud hands it back as the address a device polls and
 * redeems at, so it must be the origin. Measured live (card `t_dc2d87c6`,
 * evidence §5): the flow passed the ladder base, so the dev hub stored
 * `http://192.168.1.2:16161/api` as the device's LAN URL.
 */
export function hubOrigin(baseUrl: string): string {
  return baseUrl.replace(/\/+$/, '').replace(/\/api$/i, '');
}

/** The body `POST /api/link/claim` expects — key order irrelevant, fields are not. */
export function linkClaimBody(input: {
  deviceCode: string;
  hubPublicId: string;
  devicePubKey: string;
  lanUrl?: string | null;
  deviceName?: string | null;
  platform?: string | null;
  deviceType?: string | null;
}): Record<string, string> {
  const body: Record<string, string> = {
    code: input.deviceCode,
    hubPublicId: input.hubPublicId,
    devicePubKey: input.devicePubKey,
  };
  if (input.lanUrl) body.lanUrl = input.lanUrl;
  if (input.deviceName) body.deviceName = input.deviceName;
  if (input.platform) body.platform = input.platform;
  if (input.deviceType) body.deviceType = input.deviceType;
  return body;
}

/** The body `POST /api/hub/grants` expects (the direct-post fallback). */
export function hubGrantPostBody(input: {
  grant: string;
  devicePubKey: string;
  claimId?: string | null;
}): Record<string, string> {
  const body: Record<string, string> = {
    grant: input.grant,
    devicePubKey: input.devicePubKey,
  };
  if (input.claimId) body.claimId = input.claimId;
  return body;
}

/**
 * Does this account hold a role on the hub the cloud answered for?
 *
 * Used by the sheet's pre-read: `GET /api/oauth2/userinfo` names every hub the
 * account belongs to, and the sheet states the role *before* the confirm. The
 * hub is matched by name (the cloud's hub rows and the hub's own identity route
 * carry different ids), which is why this is only ever used for wording: the
 * authoritative role is the one the hub records when the account is linked.
 */
export function roleFromUserinfo(userinfo: unknown, hubName: string | null | undefined): string | null {
  if (!userinfo || typeof userinfo !== 'object') return null;
  const hubs = (userinfo as Record<string, unknown>).hubs;
  if (!Array.isArray(hubs)) return null;
  const wanted = (hubName ?? '').trim().toLowerCase();
  if (!wanted) return null;
  for (const entry of hubs) {
    if (!entry || typeof entry !== 'object') continue;
    const row = entry as Record<string, unknown>;
    const name = stringOrNull(row.name)?.trim().toLowerCase();
    if (name === wanted) return stringOrNull(row.role);
  }
  return null;
}
